import React, { useEffect, useRef } from 'react';
import './HowItWorks.css';

const STEPS = [
  {
    number: '01',
    title: 'Share Your Details',
    desc: 'Send your birth date, time, and place on chat. Our team responds within minutes.',
    icon: <ShareIcon />,
  },
  {
    number: '02',
    title: 'Chart Analysis',
    desc: 'Our VTD certified astrologer prepares your complete birth chart and dasha analysis.',
    icon: <ChartIcon />,
  },
  {
    number: '03',
    title: 'Live Session',
    desc: 'One-on-one consultation via Google Meet or phone call — in Telugu, Hindi, or English.',
    icon: <SessionIcon />,
  },
  {
    number: '04',
    title: 'Get Your Answers',
    desc: 'Receive precise predictions, personalised remedies, and a clear roadmap for your life.',
    icon: <AnswerIcon />,
  },
];

const HowItWorks = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const items = sectionRef.current?.querySelectorAll('.hiw-step');
    if (!items?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('hiw-step--visible'); observer.unobserve(e.target); }
      }),
      { threshold: 0.2 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="hiw" id="how-it-works" aria-label="How VTD Astrology works" ref={sectionRef}>
      <div className="hiw__inner">
        <div className="hiw__header">
          <p className="section-eyebrow">Simple Process</p>
          <h2 className="section-title">How It Works</h2>
          <p className="section-sub">From first message to life-changing insight — in 4 steps</p>
          <div className="gold-divider" />
        </div>

        <div className="hiw__steps">
          {STEPS.map((step, i) => (
            <div key={step.number} className="hiw-step" style={{ transitionDelay: `${i * 120}ms` }}>
              <div className="hiw-step__icon-wrap">
                <div className="hiw-step__icon">{step.icon}</div>
                {i < STEPS.length - 1 && <div className="hiw-step__connector" aria-hidden="true" />}
              </div>
              <div className="hiw-step__num">{step.number}</div>
              <h3 className="hiw-step__title">{step.title}</h3>
              <p className="hiw-step__desc">{step.desc}</p>
            </div>
          ))}
        </div>

        <div className="hiw__cta">
          <a href="/chat" className="hiw__cta-btn">
            Chat with Tilak Now →
          </a>
        </div>
      </div>
    </section>
  );
};

function ShareIcon() {
  return (
    <svg viewBox="0 0 44 44" width="38" height="38" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 4 L22 28"/>
      <path d="M14 12 L22 4 L30 12"/>
      <path d="M8 22 L8 38 L36 38 L36 22"/>
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg viewBox="0 0 44 44" width="38" height="38" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="22" cy="22" r="16"/>
      <line x1="22" y1="22" x2="22" y2="8"/>
      <line x1="22" y1="22" x2="32" y2="28"/>
      <circle cx="22" cy="22" r="3" fill="#FFD700" stroke="none"/>
      <path d="M10 14 Q14 10 18 14" stroke="#FFD700" strokeWidth="1.5" opacity="0.6"/>
    </svg>
  );
}

function SessionIcon() {
  return (
    <svg viewBox="0 0 44 44" width="38" height="38" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 8 L36 8 Q40 8 40 12 L40 28 Q40 32 36 32 L26 32 L18 40 L18 32 L8 32 Q4 32 4 28 L4 12 Q4 8 8 8 Z"/>
      <line x1="13" y1="18" x2="31" y2="18"/>
      <line x1="13" y1="24" x2="24" y2="24"/>
    </svg>
  );
}

function AnswerIcon() {
  return (
    <svg viewBox="0 0 44 44" width="38" height="38" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 4 L26 16 L40 16 L28 24 L32 38 L22 30 L12 38 L16 24 L4 16 L18 16 Z"/>
      <circle cx="22" cy="22" r="4" fill="#FFD700" opacity="0.8" stroke="none"/>
    </svg>
  );
}

export default HowItWorks;
